import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown } from "lucide-react";
import { getAllPhotos } from "../data/photos";

const ALL_PHOTOS = getAllPhotos();

// Pick first photo per category as a preview
const PREVIEWS = ALL_PHOTOS.reduce((acc, p) => {
  if (!acc[p.category]) acc[p.category] = p;
  return acc;
}, {});

const HERO_IMG =
  "https://d2f8l4t0zpiyim.cloudfront.net/000_clients/1083870/page/h800-1083870CubKoDJf.jpg";

const CATEGORIES = [
  { key: "portraits",    label: "Retratos",   number: "01" },
  { key: "street",       label: "Calle",      number: "02" },
  { key: "architecture", label: "Edificios",  number: "03" },
];

export default function Home() {
  const previews = PREVIEWS;

  return (
    <div data-testid="home-page" className="min-h-screen">
      {/* HERO */}
      <section className="relative min-h-screen flex items-end pb-20 pt-32 px-6 md:px-12">
        <div className="absolute inset-0 -z-10">
          <img
            src={HERO_IMG}
            alt="Retrato editorial monocromo"
            className="w-full h-full object-cover opacity-50"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink-950 via-ink-950/60 to-ink-950/30" />
        </div>

        <div className="max-w-7xl mx-auto w-full grid grid-cols-12 gap-6">
          <div className="col-span-12 md:col-span-8 reveal">
            <p className="eyebrow mb-6" data-testid="hero-eyebrow">
              Portafolio · 2026
            </p>
            <h1 className="font-serif-display text-paper text-5xl sm:text-7xl lg:text-8xl leading-[0.95] tracking-tight">
              A través<br />
              <span className="italic text-paper-dim">de la lente.</span>
            </h1>
            <p className="mt-8 text-paper-dim font-light text-lg max-w-xl leading-relaxed">
              Soy <span className="text-paper">Paco Revueltas</span>. Aquí comparto mis
              momentos favoritos capturados en una imagen. Cada foto es una historia,
              un sentimiento, una emoción.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/galeria" className="btn-primary" data-testid="hero-cta-gallery">
                Ver galería <ArrowRight size={14} strokeWidth={1.5} />
              </Link>
              <Link to="/contacto" className="btn-ghost" data-testid="hero-cta-contact">
                Contacto
              </Link>
            </div>
          </div>

          <div className="hidden md:flex col-span-4 items-end justify-end">
            <div className="text-right">
              <div className="eyebrow mb-2">Capítulos</div>
              <div className="font-mono-ed text-xs text-paper-dim space-y-1">
                <div>01 — Retratos</div>
                <div>02 — Calle</div>
                <div>03 — Edificios</div>
              </div>
            </div>
          </div>

          <div className="col-span-12 mt-16 flex items-center gap-3 text-paper-mute eyebrow">
            <ArrowDown size={14} /> Desplazar
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="py-24 md:py-32 px-6 md:px-12 border-t border-ink-700">
        <div className="max-w-7xl mx-auto grid grid-cols-12 gap-10 md:gap-16 items-start">
          {/* Portrait — asymmetric, smaller left column */}
          <div className="col-span-12 sm:col-span-7 md:col-span-4 lg:col-span-3 reveal">
            <div className="relative max-w-[280px] md:max-w-none">
              <div className="overflow-hidden bg-ink-900 aspect-[4/5]">
                <img
                  src={`${process.env.PUBLIC_URL || ""}/paco-portrait.jpg`}
                  alt="Paco Revueltas con su cámara"
                  className="w-full h-full object-cover"
                  data-testid="paco-portrait"
                  loading="lazy"
                />
              </div>
              <div className="absolute -bottom-3 left-3 right-3 px-3 py-1.5 bg-ink-950 border border-ink-700">
                <p className="font-mono-ed text-[0.6rem] tracking-[0.22em] uppercase text-paper-dim">
                  Detrás de la cámara
                </p>
              </div>
            </div>
          </div>

          {/* Text — wider right column */}
          <div className="col-span-12 md:col-span-8 lg:col-span-9">
            <p className="eyebrow mb-4">Bienvenida</p>
            <p className="mt-3 font-mono-ed text-xs text-paper-mute mb-8">
              Francisco "Paco" Revueltas<br />Fotógrafo
            </p>
            <p className="font-serif-display text-2xl sm:text-3xl text-paper leading-relaxed tracking-tight">
              <span className="italic text-paper-dim">"Bienvenido a mi mundo a través de la lente. En este portafolio,
              comparto con ustedes mis momentos favoritos capturados en una imagen.
              Cada foto es una historia, un sentimiento, una emoción.</span>
            </p>
            <p className="mt-6 font-serif-display text-2xl sm:text-3xl text-paper leading-relaxed tracking-tight">
              <span className="italic text-paper-dim">Desde la serenidad de un amanecer hasta la intensidad de un
              instante fugaz, mi cámara ha sido testigo de la belleza que nos
              rodea. Aquí les invito a recorrer conmigo un viaje visual por la
              vida, la naturaleza y la emoción humana. Espero que disfruten de
              este recorrido a través de mis ojos."</span>
            </p>
            <p className="mt-8 font-mono-ed text-xs tracking-[0.22em] uppercase text-paper-mute">— Paco Revueltas</p>
          </div>
        </div>
      </section>

      {/* CATEGORIES PREVIEW */}
      <section className="py-24 px-6 md:px-12 border-t border-ink-700">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-end justify-between mb-12">
            <div>
              <p className="eyebrow mb-3">Series</p>
              <h2 className="font-serif-display text-paper text-4xl sm:text-5xl tracking-tight">
                Tres formas de mirar
              </h2>
            </div>
            <Link to="/galeria" className="hidden md:inline-flex font-mono-ed text-[0.72rem] tracking-[0.22em] uppercase text-paper-dim hover:text-paper transition-colors items-center gap-2" data-testid="series-see-all">
              Ver todas <ArrowRight size={14} />
            </Link>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {CATEGORIES.map((c) => {
              const p = previews[c.key];
              return (
                <Link
                  key={c.key}
                  to={`/galeria?cat=${c.key}`}
                  className="group block"
                  data-testid={`series-card-${c.key}`}
                >
                  <div className="photo-card aspect-[4/5]">
                    {p ? (
                      <img src={p.image_data} alt={p.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-ink-900" />
                    )}
                  </div>
                  <div className="mt-4 flex items-start justify-between">
                    <div>
                      <div className="font-serif-display text-paper text-2xl tracking-tight">{c.label}</div>
                      <div className="eyebrow mt-1">Capítulo {c.number}</div>
                    </div>
                    <ArrowRight className="text-paper-dim group-hover:text-paper transition-colors mt-1" size={18} strokeWidth={1.25} />
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 px-6 md:px-12 border-t border-ink-700">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row items-start md:items-end justify-between gap-8">
          <div>
            <p className="eyebrow mb-3">Conversemos</p>
            <h3 className="font-serif-display text-paper text-4xl sm:text-5xl tracking-tight max-w-xl">
              Si una imagen te detuvo,<br /><span className="italic text-paper-dim">escríbeme.</span>
            </h3>
          </div>
          <Link to="/contacto" className="btn-primary" data-testid="footer-cta-contact">
            Contacto <ArrowRight size={14} strokeWidth={1.5} />
          </Link>
        </div>
      </section>
    </div>
  );
}
